import React, { useEffect, useRef, useState } from 'react'
import { MetaData } from 'components/MetaData'
import { FadeIn } from 'lib/ui'
import { useIsMobile } from 'hooks/responsive'
import { PRODUCT_IMAGE_URL } from 'lib/core/resources/constants'
import { useStoreView } from 'hooks/useStoreView'
import { Breadcrumbs } from 'components/Breadcrumbs'
import { useProductContext } from 'scenes/Product'
import { DescriptionTabs } from '../DescriptionTabs'
import { ProductList } from '../ProductList'
import { MediaGallery } from '../MediaGallery'
import { AddToWishlist } from 'components/AddToWishlist'
import { ReviewsModal } from 'components/Reviews'

import {
  ContainerPrimary,
  ContainerBottom,
  ProductImageWrapper,
  WishListWrapper
} from '../Styled'
import { useHistory } from 'react-router-dom'
import { ProductData, MagentoMediaGalleryEntry } from 'lib/types'
import { OptionsSection } from '../OptionsSection'
import { TrackViewProducts } from '../../../../lib/gtm/index'

interface Props {
  product: ProductData
  fromProductSelector: boolean
  forceOneTimePurchase: boolean
}

export const Layout = ({
  fromProductSelector,
  forceOneTimePurchase,
  product
}: Props) => {
  const {
    id,
    name,
    image,
    sku,
    descriptionHtml,
    mediaGalleryItems,
    customAttribute,
    customAttributeList,
    variants,
    relatedProducts
  } = product
  const [productImage, setProductImage] = useState(image.default.url)
  const [mediaGalleryIndex, setMediaGalleryIndex] = useState(0)
  const { isSofiaAndGrace } = useStoreView()
  const isMobile = useIsMobile()
  const tabsRef = useRef<HTMLDivElement>(null)
  const { setDescriptionTabOpen } = useProductContext()
  const [sizingChart] = customAttribute?.sizing_chart ?? []
  const [absorbency] = customAttribute?.primary_bladder_leakage ?? []
  const [discreet] = customAttribute?.discreet ?? []
  const [overnight] = customAttribute?.overnight ?? []
  const [innerLegCuff] = customAttribute?.innerLegCuff ?? []
  const [brand] = customAttribute?.manufacturer ?? []
  const history = useHistory<{ openReviews?: boolean } | undefined>()
  const reviewsTabOpen = history.location.state?.openReviews
  const isConfigurable = variants.length > 0
  const [subscriptionsEligible] = customAttribute?.subscription_eligible ?? []
  const isSubscriptionEligible = subscriptionsEligible !== 'No'

  const handleGalleryItem = (item: MagentoMediaGalleryEntry, index: number) => {
    setMediaGalleryIndex(index)
    setProductImage(`${PRODUCT_IMAGE_URL}${item.file}`)
  }

  useEffect(() => {
    setProductImage(image.default.url)
  }, [image, setProductImage])

  useEffect(() => {
    if (reviewsTabOpen) {
      setDescriptionTabOpen(1)
      if (tabsRef.current !== null) {
        tabsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
    // eslint-disable-next-line
  }, [])

  return (
    <>
      <MetaData
        type='PDP'
      />
      <TrackViewProducts product={product} />
      <div>
        <FadeIn>
          <ContainerPrimary
            isSofia={isSofiaAndGrace}
          >
            <div className='breadrumbs'>
              <Breadcrumbs />
            </div>

            {!isMobile ? (
              <>
                <div>
                  <div>
                    <ProductImageWrapper
                      isSofia={isSofiaAndGrace}
                    >
                      <MediaGallery
                        mediaGalleryItems={mediaGalleryItems}
                        productImage={productImage}
                        handleGalleryItem={handleGalleryItem}
                        mediaGalleryIndex={mediaGalleryIndex}
                        setMediaGalleryIndex={setMediaGalleryIndex}
                        isConfigurable={isConfigurable}
                      />
                    </ProductImageWrapper>
                  </div>
                  <div className='product__information'>
                    {isSubscriptionEligible && (
                      <div className='promo-tag'>Schedule & Save 10%</div>
                    )}
                    <div>
                      <WishListWrapper>
                        <p>{brand}</p>
                        <AddToWishlist
                          sku={sku}
                        />
                      </WishListWrapper>
                      <h1>{name}</h1>
                      <p className='product__status'>In Stock</p>
                      <OptionsSection
                        sizingChart={sizingChart}
                        customAttributes={customAttribute}
                        fromProductSelector={fromProductSelector}
                        productImage={productImage}
                        forceOneTimePurchase={forceOneTimePurchase}
                        setProductImage={setProductImage}
                        product={product}
                      />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div>
                <h1>{name}</h1>
                <ProductImageWrapper>
                  <MediaGallery
                    mediaGalleryItems={mediaGalleryItems}
                    productImage={productImage}
                    handleGalleryItem={handleGalleryItem}
                    mediaGalleryIndex={mediaGalleryIndex}
                    setMediaGalleryIndex={setMediaGalleryIndex}
                    isConfigurable={isConfigurable}
                  />
                </ProductImageWrapper>
                <OptionsSection
                  sizingChart={sizingChart}
                  customAttributes={customAttribute}
                  fromProductSelector={fromProductSelector}
                  productImage={productImage}
                  forceOneTimePurchase={forceOneTimePurchase}
                  setProductImage={setProductImage}
                  product={product}
                />
              </div>
            )}
          </ContainerPrimary>
          <ContainerBottom
            isSofia={isSofiaAndGrace}
            ref={tabsRef}
          >
            <DescriptionTabs
              id={id}
              productDescription={descriptionHtml}
              absorbency={absorbency}
              discreet={discreet}
              overnight={overnight}
              innerLegCuff={innerLegCuff}
              customAttributes={customAttributeList}
            />
          </ContainerBottom>
          {isSofiaAndGrace && (
            <ProductList
              productLinks={relatedProducts}
              title='You’ll Also Love'
            />
          )}
          <ReviewsModal />
        </FadeIn>
      </div>
    </>
  )
}
